import React from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import {
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon,
  Groups as GroupsIcon,
  Tablet as TabletIcon,
  Map as MapIcon,
  RecordVoiceOver as QuoteIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { Box, Container, Typography, Grid, Paper, Divider, Chip, Avatar } from '@mui/material';

// Placeholder SVG intégré (évite les erreurs 404)
const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;base64,' +
  btoa(`
<svg xmlns="http://www.w3.org/2000/svg" width="800" height="500" viewBox="0 0 800 500">
  <rect width="800" height="500" fill="#f0f0f0"/>
  <text x="400" y="250" font-family="Arial" font-size="24" fill="#999" text-anchor="middle">Image illustratrice</text>
</svg>
`);

const ActualiteRPPCUB = () => {
  // Fonction de chemin d'image avec fallback
  const getImagePath = (fileName) => {
    if (!fileName || fileName.trim() === '') return PLACEHOLDER_IMAGE;
    const cleanName = fileName.trim();
    const extensions = ['.jpg', '.jpeg', '.png', '.webp', '.JPG', '.JPEG', '.PNG'];
    const hasExtension = extensions.some((ext) => cleanName.endsWith(ext));
    const path = hasExtension
      ? `/assets/rppcub/${cleanName}`
      : `/assets/rppcub/${cleanName}.jpg`;
    return path;
  };

  // Données des photos
  const photos = [
    {
      src: 'premier-ministre.jpeg',
      title: 'Son Excellence le Premier Ministre Nestor Ntahontuye entouré du Ministre des Finances et du Gouverneur de Bujumbura',
      alt: 'Premier Ministre et autorités',
    },
    {
      src: 'dg-insbu.jpeg',
      title: 'Le Directeur Général de l\'INSBU présente le dispositif technique du RPPCUB',
      alt: 'DG de l\'INSBU en présentation',
    },
    {
      src: 'participants-1.jpeg',
      title: 'La présence des gouverneurs de provinces',
      alt: 'Participants',
    },
    {
      src: 'participants-2.jpeg',
      title: 'Vue d\'ensemble de la salle lors du lancement officiel',
      alt: 'Vue de la salle',
    },
  ];

  // Objectifs spécifiques (pour affichage en grille)
  const objectifs = [
    'Identifier et localiser toutes les structures bâties et parcelles non bâties dans les zones ciblées',
    'Identifier le propriétaire et recueillir ses informations d’identification et de mise en valeur',
    'Constituer une base de données interopérable avec un identifiant unique par propriété',
    'Déterminer le statut de propriété et la possession des documents légaux',
    'Évaluer les fonds nécessaires pour la 1ère phase de numérisation des titres',
    'Produire une cartographie exhaustive et actualisée des propriétés',
    'Élaborer des statistiques et cartes thématiques sur la propriété foncière',
    'Digitaliser le cadastre urbain avec des titres numériques et une interface web SIG',
  ];

  // Chiffres clés pour l'encadré "En bref"
  const chiffresCles = [
    { label: 'Agents recenseurs', value: '432' },
    { label: 'Chefs d\'équipes', value: '83' },
    { label: 'Superviseurs', value: '8' },
    { label: 'Comités (pilotage + technique)', value: '' },
    { label: 'Zones urbaines initiales', value: '6 communes' },
    { label: 'Durée de collecte', value: '45 jours' },
  ];

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <NavigationBar />
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ bgcolor: 'white', borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          {/* EN-TÊTE DE L'ARTICLE */}
          <Box sx={{ p: { xs: 3, md: 5 }, borderBottom: '1px solid #e0e0e0' }}>
            {/* Badges */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="INSBU" sx={{ bgcolor: '#E30613', color: 'white', fontWeight: 'bold' }} />
              <Chip label="RPPCUB 2026" sx={{ bgcolor: '#008D36', color: 'white', fontWeight: 'bold' }} />
              <Chip label="Lancement officiel" sx={{ bgcolor: '#1976d2', color: 'white' }} />
            </Box>

            {/* Titre principal */}
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: '#1a3e1f', fontSize: { xs: '1.8rem', md: '2.5rem' }, lineHeight: 1.2, mb: 1 }}>
              Lancement officiel du Recensement des Propriétaires des Propriétés/Parcelles dans les Centres Urbains du Burundi (RPPCUB 2026)
            </Typography>
            <Typography variant="h5" sx={{ color: '#333', fontWeight: 400, mb: 2, fontSize: { xs: '1.1rem', md: '1.3rem' } }}>
              Le Gouvernement engage une réforme majeure pour la sécurisation foncière et la digitalisation du Burundi
            </Typography>

            {/* Métadonnées */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', color: '#555', fontSize: '0.95rem' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                Bujumbura, le 14 juillet 2026
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                Hôtel Royal Palace
              </Box>
            </Box>
          </Box>

          {/* CHAPÔ (introduction) */}
          <Box sx={{ p: { xs: 3, md: 5 }, bgcolor: '#f5f9f5' }}>
            <Typography variant="body1" sx={{ fontSize: '1.15rem', lineHeight: 1.8, color: '#222', fontStyle: 'italic' }}>
              <strong>Le mardi 14 juillet 2026</strong>, Son Excellence le Premier Ministre, <strong>Nestor Ntahontuye</strong>, a présidé au Royal Palace Hotel la cérémonie officielle de lancement des activités du <strong>Recensement des Propriétaires des Propriétés/Parcelles dans les Centres Urbains du Burundi (RPPCUB 2026)</strong>.
              Pilotée par l'<strong>INSBU</strong> en collaboration avec la <strong>DTFCN</strong>, cette opération d’envergure marque le démarrage d’une réforme destinée à moderniser la gouvernance foncière et à accélérer la transformation numérique du pays.
            </Typography>
          </Box>

          {/* CORPS DE L'ARTICLE */}
          <Box sx={{ p: { xs: 3, md: 5 } }}>
            {/* SECTION : CONTEXTE */}
            <Typography variant="h4" component="h2" sx={{ color: '#008D36', fontWeight: 600, mb: 2, fontSize: '1.6rem' }}>
              Un contexte marqué par des défis structurels
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              Conformément au contexte exposé par le Directeur Général de l’INSBU, cette initiative répond à une problématique bien réelle : actuellement, les structures bâties sont nombreuses mais leurs propriétaires ne sont pas systématiquement connus par les autorités administratives, le cadastre national et la Direction des titres fonciers. Une part importante des parcelles ne dispose pas de titres fonciers formels, ce qui limite la sécurisation foncière et la planification urbaine.
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
              Par ailleurs, la gestion des demandes de parcelles en milieu urbain constitue un défi quotidien. Chaque projet d’aménagement suscite un grand nombre de demandes alors qu’il manque des critères objectifs pour identifier les bénéficiaires prioritaires. L’absence d’un registre intégré des propriétés urbaines et d’un mécanisme d’actualisation automatisée empêche toute interopérabilité entre le registre de la population, le registre cadastral et le registre social.
            </Typography>

            <Divider sx={{ my: 4 }} />

            {/* SECTION : ZONES CONCERNÉES */}
            <Typography variant="h4" component="h2" sx={{ color: '#008D36', fontWeight: 600, mb: 2, fontSize: '1.6rem' }}>
              Zones concernées par cette première phase
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              Le RPPCUB 2026 débute dans les grandes agglomérations urbaines des communes de :
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {['Ntahangwa, Mukaza et Mugere (Province de Bujumbura)', 'Gitega (Province Gitega)', 'Ngozi (Province Butanyerera)', 'Rumonge (Province Burunga)'].map((zone, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f0f7f0', p: 1.5, borderRadius: 1 }}>
                    <LocationIcon sx={{ color: '#008D36', mr: 1.5 }} />
                    <Typography variant="body1">{zone}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
              Cette phase préliminaire précède la généralisation progressive à toutes les autres communes du pays.
            </Typography>

            <Divider sx={{ my: 4 }} />

            {/* SECTION : OBJECTIFS STRATÉGIQUES */}
            <Typography variant="h4" component="h2" sx={{ color: '#008D36', fontWeight: 600, mb: 2, fontSize: '1.6rem' }}>
              Objectifs stratégiques du recensement
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              <strong>Objectif général :</strong> mettre en place, dans un premier temps, une base de données exhaustive, fiable et géoréférencée des propriétés/parcelles situées dans les principales agglomérations urbaines du Burundi. Cette base doit permettre, à terme, l’amélioration de la planification urbaine et de la gouvernance foncière à travers l’octroi de titres de propriété numériques à tous les propriétaires.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
              Objectifs spécifiques déclinés par l’INSBU :
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {objectifs.map((obj, idx) => (
                <Grid item xs={12} md={6} key={idx}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, bgcolor: '#f9f9f9', p: 1.5, borderRadius: 1 }}>
                    <CheckIcon sx={{ color: '#008D36', fontSize: '1.2rem', mt: 0.2 }} />
                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>{obj}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* SECTION : MÉTHODOLOGIE CAPI */}
            <Typography variant="h4" component="h2" sx={{ color: '#008D36', fontWeight: 600, mb: 2, fontSize: '1.6rem' }}>
              Une méthodologie moderne et innovante (CAPI)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
              <Box sx={{ flex: 2 }}>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Sur le plan méthodologique, l’INSBU a opté pour une approche de dénombrement exhaustif des propriétaires (personnes physiques et morales). La collecte des données sera assistée par ordinateur (<strong>CAPI</strong>) via la plateforme <strong>Survey Solutions</strong> (Banque Mondiale). Un serveur propre à l’INSBU est déjà disponible.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Les agents recenseurs, équipés de <strong>tablettes</strong>, enregistreront les réponses en temps réel. Cette technologie réduit les coûts, améliore la qualité des données et permet un contrôle de cohérence avancé (GPS, photos, audio).
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                  Pour le <strong>géoréférencement</strong>, le système permet de prendre les coordonnées, calculer la superficie et dessiner un polygone sur une carte. Ces informations serviront à créer une cartographie précise et à éviter les doublons.
                </Typography>
              </Box>
              <Box sx={{ flex: 1, bgcolor: '#e8f5e9', p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <TabletIcon sx={{ fontSize: 60, color: '#008D36' }} />
                <Typography variant="caption" sx={{ mt: 1, textAlign: 'center' }}>Collecte assistée par tablette</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* SECTION : GOUVERNANCE ET RESSOURCES */}
            <Typography variant="h4" component="h2" sx={{ color: '#008D36', fontWeight: 600, mb: 2, fontSize: '1.6rem' }}>
              Dispositif de gouvernance et ressources humaines
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              La gouvernance du RPPCUB s’articule autour de trois niveaux :
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%', borderTop: '4px solid #E30613' }}>
                  <GroupsIcon sx={{ color: '#E30613', fontSize: 30 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>Comité de pilotage</Typography>
                  <Typography variant="body2">Ministres et Hauts cadres : Validation du protocole, orientations stratégiques.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%', borderTop: '4px solid #008D36' }}>
                  <GroupsIcon sx={{ color: '#008D36', fontSize: 30 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>Comité technique</Typography>
                  <Typography variant="body2">Hauts cadres : Conception, méthodologie, organisation et restitution.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%', borderTop: '4px solid #1976d2' }}>
                  <GroupsIcon sx={{ color: '#1976d2', fontSize: 30 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>Administration territoriale</Typography>
                  <Typography variant="body2">Gouverneurs, Administrateurs, Chefs de zones – Accueil, facilitation, sécurisation et sensibilisation.</Typography>
                </Paper>
              </Grid>
            </Grid>

          

            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              Les agents bénéficieront d’une formation de trois jours. Une supervision rapprochée sera assurée par 8 superviseurs, tandis que la coordination informatique sera assurée à distance. La collecte durera <strong>1,5 mois (45 jours)</strong>.
            </Typography>

            <Divider sx={{ my: 4 }} />

            {/* SECTION : MESSAGE DU PREMIER MINISTRE */}
            <Typography variant="h4" component="h2" sx={{ color: '#008D36', fontWeight: 600, mb: 2, fontSize: '1.6rem' }}>
              Message du Premier Ministre : Transparence, Sécurité et Mobilisation générale
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              Dans son allocution, le Chef du Gouvernement a insisté sur le caractère <strong>transparent, sécurisé et confidentiel</strong> de cette opération. Il a rassuré les propriétaires fonciers que les informations collectées seront exclusivement utilisées dans l’intérêt des citoyens.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              Son Excellence Nestor Ntahontuye a fait comprendre que l’objectif est de constituer un registre foncier moderne, fiable et entièrement numérisé, protégeant les biens contre la spoliation et dotant l’État d’un outil performant pour planifier le développement urbain, les infrastructures et les services essentiels.
            </Typography>

            {/* Citation en encadré */}
            <Box sx={{ bgcolor: '#e8f5e9', p: 3, borderRadius: 2, border: '1px solid #008D36', mb: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <QuoteIcon sx={{ color: '#008D36', fontSize: 40, flexShrink: 0 }} />
              <div>
                <Typography variant="h6" sx={{ fontStyle: 'italic', color: '#1a3e1f', mb: 0.5 }}>
                  « Ensemble, soutenons et prônons un recensement des propriétaires des propriétés et parcelles pour un Burundi émergent et développé »
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a3e1f' }}>
                  — Son Excellence Nestor Ntahontuye, Premier Ministre
                </Typography>
              </div>
            </Box>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              Soulignant que la sécurisation foncière est un pilier de la Vision nationale <strong>« Burundi, Pays Émergent en 2040 et Pays Développé en 2060 »</strong>, il a appelé à une mobilisation générale : propriétaires, autorités locales, leaders communautaires et médias sont invités à jouer un rôle clé.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              Réaffirmant l’engagement du Gouvernement à garantir à chaque citoyen la reconnaissance et la sécurisation de ses droits de propriété, le Premier Ministre a officiellement déclaré ouvertes les activités du RPPCUB 2026.
            </Typography>

            <Divider sx={{ my: 4 }} />

            {/* SECTION : GALERIE PHOTOS */}
            <Typography variant="h4" component="h2" sx={{ color: '#008D36', fontWeight: 600, mb: 3, fontSize: '1.6rem' }}>
              En images : la cérémonie de lancement
            </Typography>
            <Grid container spacing={3}>
              {photos.map((photo, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box sx={{ textAlign: 'center' }}>
                    <img
                      src={getImagePath(photo.src)}
                      alt={photo.alt}
                      style={{ width: '100%', height: 'auto', borderRadius: 8, boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
                      onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMAGE; }}
                    />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                      {photo.title}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* CONCLUSION */}
            <Box sx={{ bgcolor: '#f5f9f5', p: 3, borderRadius: 2, border: '1px solid #008D36' }}>
              <Typography variant="h5" sx={{ color: '#008D36', fontWeight: 600, mb: 2 }}>
                Un pas décisif pour la sécurisation foncière et la digitalisation
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                Le lancement du RPPCUB 2026 marque une étape fondamentale dans la modernisation du cadastre burundais. En combinant des technologies de pointe (CAPI, géoréférencement) et une mobilisation nationale sans précédent, le Burundi se dote d’un outil de gouvernance foncière indispensable pour sa Vision 2040-2060.
              </Typography>
             
            </Box>
          </Box>
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
};

export default ActualiteRPPCUB;